import * as React from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { LucideIcon } from './LucideIcon';
import type { Geopoint as Interface } from '../../design/components';
import { parseGeopoint, formatGeopoint, initLeafletIcon, DEFAULT_CENTER } from '../shared/geopoint';

initLeafletIcon();

async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
      { headers: { 'User-Agent': 'mksys' } }
    );
    if (!response.ok) return null;
    const data = await response.json();
    return data.display_name || null;
  } catch {
    return null;
  }
}

interface AddressSuggestion {
  label: string;
  lat: number;
  lng: number;
}

async function searchAddresses(query: string): Promise<AddressSuggestion[]> {
  if (!query || query.length < 3) return [];
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5`,
      { headers: { 'User-Agent': 'mksys' } }
    );
    if (!response.ok) return [];
    const data = await response.json();
    return data.map((item: { display_name: string; lat: string; lon: string }) => ({
      label: item.display_name,
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
    }));
  } catch {
    return [];
  }
}

function MapClickHandler({ onLocationChange }: { onLocationChange: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onLocationChange(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function MapViewUpdater({ center }: { center: [number, number] }) {
  const map = useMap();
  const prevCenter = React.useRef<[number, number] | null>(null);

  React.useEffect(() => {
    if (prevCenter.current && (prevCenter.current[0] !== center[0] || prevCenter.current[1] !== center[1])) {
      map.setView(center);
    }
    prevCenter.current = center;
  }, [center, map]);

  return null;
}

export const Geopoint: React.FC<Interface> = ({
  name,
  description,
  state,
  hidden,
  readonly,
  disabled,
  required,
  error,
}) => {
  const [value, setValue] = state || ['', () => {}];
  const [addressInput, setAddressInput] = React.useState('');
  const [suggestions, setSuggestions] = React.useState<AddressSuggestion[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const position = parseGeopoint(value) || DEFAULT_CENTER;
  const skipReverseGeocode = React.useRef(false);
  const debounceTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    if (skipReverseGeocode.current) {
      skipReverseGeocode.current = false;
      return;
    }
    const coords = parseGeopoint(value);
    if (coords) {
      reverseGeocode(coords[0], coords[1]).then((addr) => {
        if (addr) setAddressInput(addr);
      });
    }
  }, [value]);

  const handleLocationChange = (lat: number, lng: number) => {
    if (readonly || disabled) return;
    setValue(formatGeopoint(lat, lng));
  };

  const handleCoordinateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const handleAddressInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setAddressInput(newValue);
    setShowSuggestions(true);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(async () => {
      if (newValue.length >= 3) {
        setLoading(true);
        const results = await searchAddresses(newValue);
        setSuggestions(results);
        setLoading(false);
      } else {
        setSuggestions([]);
      }
    }, 300);
  };

  const handleSuggestionSelect = (suggestion: AddressSuggestion) => {
    skipReverseGeocode.current = true;
    setAddressInput(suggestion.label);
    setValue(formatGeopoint(suggestion.lat, suggestion.lng));
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div className={cn('flex flex-col gap-2', hidden && 'hidden')} data-disabled={disabled}>
      {name && (
        <Label htmlFor={name} className={cn(error && 'text-destructive')}>
          {name}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      {/* Coordinate input */}
      <div className="relative flex items-center">
        <div
          className={cn(
            'flex w-full items-center rounded-t-md border bg-background px-3 py-2 h-9 gap-2',
            'focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]',
            error && 'border-destructive',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          <LucideIcon name="MapPin" size="small" className={cn(error ? 'text-destructive' : 'text-muted-foreground')} />
          <input
            id={name}
            name={name}
            type="text"
            value={value}
            onChange={handleCoordinateChange}
            disabled={disabled}
            readOnly={readonly}
            placeholder="lat,lng (e.g., 40.7128,-74.0060)"
            className={cn(
              'flex-1 bg-transparent text-sm outline-none',
              'placeholder:text-muted-foreground',
              disabled && 'cursor-not-allowed',
              readonly && 'cursor-default'
            )}
          />
        </div>
      </div>

      {/* Address search */}
      <div className="relative -mt-2">
        <div
          className={cn(
            'flex w-full items-center rounded-b-md border border-t-0 bg-background px-3 py-2 h-9 gap-2',
            'focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          <LucideIcon name="Search" size="small" className="text-muted-foreground" />
          <input
            id={`${name}-address`}
            type="text"
            value={addressInput}
            onChange={handleAddressInputChange}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            disabled={disabled || readonly}
            placeholder="Search address..."
            className={cn(
              'flex-1 bg-transparent text-sm outline-none',
              'placeholder:text-muted-foreground',
              disabled && 'cursor-not-allowed'
            )}
          />
          {loading && (
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          )}
        </div>

        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-auto">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSuggestionSelect(suggestion)}
                className="w-full px-3 py-2 text-sm text-left hover:bg-accent focus:bg-accent focus:outline-none"
              >
                {suggestion.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Map */}
      <div
        className={cn(
          'relative z-0 h-[300px] rounded-md overflow-hidden border',
          error ? 'border-destructive' : 'border-input',
          disabled && 'opacity-50 pointer-events-none',
          readonly && 'pointer-events-none'
        )}
      >
        <MapContainer
          center={position}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker
            position={position}
            draggable={!readonly && !disabled}
            eventHandlers={{
              dragend: (e) => {
                const marker = e.target;
                const pos = marker.getLatLng();
                handleLocationChange(pos.lat, pos.lng);
              },
            }}
          />
          <MapClickHandler onLocationChange={handleLocationChange} />
          <MapViewUpdater center={position} />
        </MapContainer>
      </div>

      {(error || description) && (
        <p className={cn('text-sm', error ? 'text-destructive' : 'text-muted-foreground')}>
          {error || description}
        </p>
      )}
    </div>
  );
};
